import React, { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import * as XLSX from 'xlsx';
import { ImportSourceType, fetchPlannerPlans } from '../../../services/importService';
import {
  parseMsProjectXml, ImportTaskMeta, fetchMondayBoards, fetchMondayItems,
  fetchSmartsheetSheets, fetchSmartsheetSheet,
  fetchJiraProjects, fetchJiraIssues,
  fetchAsanaWorkspaces, fetchAsanaProjects, fetchAsanaTasks,
  fetchClickUpTeams, fetchClickUpSpaces, fetchClickUpLists, fetchClickUpTasks,
  fetchTrelloBoards, fetchTrelloCards,
  fetchProjectOnlineProjects, fetchProjectOnlineTasks,
} from '../../../services/importConnectors';

// WBS hierarchy carried alongside a rowset (Microsoft Project XML and, later,
// Project Online): which collection each task row belongs to and its
// predecessor links, resolved to dependency records after import.
export interface ImportHierarchy {
  collections: string[];
  taskMeta: ImportTaskMeta[];
  dependencyCount: number;
}

interface Props {
  pca: IPublicClientApplication;
  source: number;
  onConnect: (data: unknown) => void;
}

// Shared shape emitted by file/API connectors so MappingStep, PreviewStep,
// and RunStep can drive off the same headers + rows model regardless of
// which source produced them.
export interface ConnectionPayload {
  kind: 'planner' | 'rowset' | 'projectonline';
  planId?: string; planTitle?: string;
  pwaUrl?: string;
  fileName?: string;
  headers?: string[];
  rows?: unknown[][];                                // each row is an array aligned with headers
  sourceLabel?: string;                              // e.g. "Monday.com: Sprint Board"
  hierarchy?: ImportHierarchy;                       // WBS collections + predecessor links
}

export default function ConnectionStep({ pca, source, onConnect }: Props) {
  // Planner
  const [plans, setPlans]       = useState<{ id: string; title: string }[]>([]);
  const [selPlan, setSelPlan]   = useState('');
  const [loading, setLoading]   = useState(false);
  // Project Online (PWA OData via the import proxy)
  const [pwaUrl, setPwaUrl]     = useState('');
  const [poProjects, setPoProjects] = useState<{ id: string; name: string; start: string | null; finish: string | null }[]>([]);
  const [selPoProject, setSelPoProject] = useState('');
  // File upload
  const [fileName, setFileName] = useState('');
  const [fileKind, setFileKind] = useState<'excel' | 'xml' | 'mpp' | null>(null);
  const [parsedRows, setParsedRows] = useState<unknown[][]>([]);
  const [headers, setHeaders]   = useState<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  // Microsoft Project XML WBS hierarchy (OutlineLevel 1 → collections).
  const [mppCollections, setMppCollections] = useState<string[]>([]);
  const [mppTaskMeta, setMppTaskMeta] = useState<ImportTaskMeta[]>([]);
  const [mppDependencyCount, setMppDependencyCount] = useState(0);
  // Monday / Smartsheet
  const [token, setToken]       = useState('');
  const [apiError, setApiError] = useState<string | null>(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [boards, setBoards]     = useState<{ id: string; name: string }[]>([]);
  const [selBoard, setSelBoard] = useState('');
  const [sheets, setSheets]     = useState<{ id: string; name: string }[]>([]);
  const [selSheet, setSelSheet] = useState('');
  const [externalReady, setExternalReady] = useState(false);  // rows+headers populated

  // Jira
  const [jiraEmail, setJiraEmail]   = useState('');
  const [jiraDomain, setJiraDomain] = useState('');
  const [jiraProjects, setJiraProjects] = useState<{ id: string; key: string; name: string }[]>([]);
  const [selJira, setSelJira] = useState('');
  // Asana
  const [asanaWorkspaces, setAsanaWorkspaces] = useState<{ gid: string; name: string }[]>([]);
  const [selAsanaWs, setSelAsanaWs] = useState('');
  const [asanaProjects, setAsanaProjects] = useState<{ gid: string; name: string }[]>([]);
  const [selAsanaProject, setSelAsanaProject] = useState('');
  // ClickUp
  const [cuTeams, setCuTeams] = useState<{ id: string; name: string }[]>([]);
  const [selCuTeam, setSelCuTeam] = useState('');
  const [cuSpaces, setCuSpaces] = useState<{ id: string; name: string }[]>([]);
  const [selCuSpace, setSelCuSpace] = useState('');
  const [cuLists, setCuLists] = useState<{ id: string; name: string }[]>([]);
  const [selCuList, setSelCuList] = useState('');
  // Trello
  const [trelloKey, setTrelloKey] = useState('');
  const [trelloToken, setTrelloToken] = useState('');
  const [trelloBoards, setTrelloBoards] = useState<{ id: string; name: string }[]>([]);
  const [selTrelloBoard, setSelTrelloBoard] = useState('');

  useEffect(() => {
    if (source === ImportSourceType.Planner) {
      setLoading(true);
      fetchPlannerPlans(pca).then(setPlans).catch(() => {}).finally(() => setLoading(false));
    }
  }, [pca, source]);

  const handleFile = async (file: File) => {
    setFileName(file.name);
    setParseError(null);
    setParsedRows([]); setHeaders([]);
    setMppCollections([]); setMppTaskMeta([]); setMppDependencyCount(0);

    const ext = file.name.toLowerCase().split('.').pop() ?? '';
    if (source === ImportSourceType.ExcelCSV) {
      setFileKind('excel');
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target?.result, { type: 'binary' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });
          const h = (rows[0] as string[]) || [];
          setHeaders(h.map(String));
          setParsedRows(rows.slice(1));
        } catch (err) {
          setParseError(err instanceof Error ? err.message : 'Failed to parse Excel/CSV file.');
        }
      };
      reader.readAsBinaryString(file);
      return;
    }

    // Project-import path: accept .xml only. Provide a clear message for .mpp.
    if (ext === 'mpp' || ext === 'mpt') {
      setFileKind('mpp');
      setParseError(
        'MPP files cannot be imported directly in the browser. Please export your project from Microsoft Project or Project Online as XML: File → Save As → XML Format (.xml). Then re-upload the .xml file.'
      );
      return;
    }
    if (ext !== 'xml') {
      setFileKind(null);
      setParseError(`Unexpected file extension "${ext}". Expected .xml (preferred) or .mpp.`);
      return;
    }

    // XML parse — OutlineLevel 1 tasks become collections, 2+ become work items,
    // and PredecessorLink elements become dependencies.
    setFileKind('xml');
    const text = await file.text();
    try {
      const parsed = parseMsProjectXml(text);
      setHeaders(parsed.headers);
      setParsedRows(parsed.rows);
      setMppCollections(parsed.collections);
      setMppTaskMeta(parsed.taskMeta);
      setMppDependencyCount(parsed.dependencyCount);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Failed to parse Project XML file.');
    }
  };

  const connectMonday = async () => {
    setApiError(null); setApiLoading(true); setExternalReady(false);
    try {
      const list = await fetchMondayBoards(token);
      setBoards(list);
      if (list.length === 0) setApiError('Token accepted but no boards were returned. Check the token permissions.');
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to connect to Monday.com.');
    } finally { setApiLoading(false); }
  };

  const loadMondayBoard = async () => {
    if (!selBoard) return;
    setApiError(null); setApiLoading(true); setExternalReady(false);
    try {
      const { headers: h, rows: r } = await fetchMondayItems(token, selBoard);
      setHeaders(h);
      setParsedRows(r);
      setExternalReady(h.length > 0);
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to load board items.');
    } finally { setApiLoading(false); }
  };

  const connectSmartsheet = async () => {
    setApiError(null); setApiLoading(true); setExternalReady(false);
    try {
      const list = await fetchSmartsheetSheets(token);
      setSheets(list);
      if (list.length === 0) setApiError('Token accepted but no sheets were returned.');
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to connect to Smartsheet.');
    } finally { setApiLoading(false); }
  };

  const loadSmartsheetSheet = async () => {
    if (!selSheet) return;
    setApiError(null); setApiLoading(true); setExternalReady(false);
    try {
      const { headers: h, rows: r } = await fetchSmartsheetSheet(token, selSheet);
      setHeaders(h);
      setParsedRows(r);
      setExternalReady(h.length > 0);
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to load sheet.');
    } finally { setApiLoading(false); }
  };

  // ── Project Online ─────────────────────────────────────────────
  const connectProjectOnline = async () => {
    setApiError(null); setApiLoading(true); setExternalReady(false);
    setPoProjects([]); setSelPoProject('');
    try {
      const url = pwaUrl.trim();
      let parsedUrl: URL;
      try { parsedUrl = new URL(url); } catch { throw new Error('Enter a valid https:// PWA site URL.'); }
      if (parsedUrl.protocol !== 'https:' || !parsedUrl.pathname.toLowerCase().includes('/sites/')) {
        throw new Error('Enter a valid Project Online PWA URL, e.g. https://yourtenant.sharepoint.com/sites/pwa');
      }
      const list = await fetchProjectOnlineProjects(pca, url);
      setPoProjects(list);
      if (list.length === 0) setApiError('Connected, but no projects are visible to your account on this PWA site.');
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to connect to Project Online.');
    } finally { setApiLoading(false); }
  };

  const loadProjectOnlineTasks = async () => {
    if (!selPoProject) return;
    setApiError(null); setApiLoading(true); setExternalReady(false);
    try {
      const parsed = await fetchProjectOnlineTasks(pca, pwaUrl.trim(), selPoProject);
      setHeaders(parsed.headers);
      setParsedRows(parsed.rows);
      setMppCollections(parsed.collections);
      setMppTaskMeta(parsed.taskMeta);
      setMppDependencyCount(parsed.dependencyCount);
      setExternalReady(true);
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to load Project Online tasks.');
    } finally { setApiLoading(false); }
  };

  const readyToConnect = (): boolean => {
    if (source === ImportSourceType.Planner) return !!selPlan;
    if (source === ImportSourceType.ProjectOnline) return externalReady;
    if (source === ImportSourceType.ProjectProfessional) return fileKind === 'xml' && parsedRows.length > 0;
    if (source === ImportSourceType.ExcelCSV) return parsedRows.length > 0;
    if (source === ImportSourceType.Monday || source === ImportSourceType.Smartsheet
        || source === ImportSourceType.Jira || source === ImportSourceType.Asana
        || source === ImportSourceType.ClickUp || source === ImportSourceType.Trello)
      return externalReady && parsedRows.length > 0;
    return false;
  };

  // ── Jira ──────────────────────────────────────────────────────
  const connectJira = async () => {
    setApiError(null); setApiLoading(true); setExternalReady(false);
    try {
      const list = await fetchJiraProjects({ email: jiraEmail.trim(), token, domain: jiraDomain.trim() });
      setJiraProjects(list);
      if (list.length === 0) setApiError('Connected, but no Jira projects are visible to this account.');
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to connect to Jira.');
    } finally { setApiLoading(false); }
  };
  const loadJiraIssues = async () => {
    if (!selJira) return;
    setApiError(null); setApiLoading(true); setExternalReady(false);
    try {
      const { headers: h, rows: r } = await fetchJiraIssues({ email: jiraEmail.trim(), token, domain: jiraDomain.trim() }, selJira);
      setHeaders(h); setParsedRows(r); setExternalReady(h.length > 0);
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to load Jira issues.');
    } finally { setApiLoading(false); }
  };

  // ── Asana ─────────────────────────────────────────────────────
  const connectAsana = async () => {
    setApiError(null); setApiLoading(true); setExternalReady(false);
    try {
      const ws = await fetchAsanaWorkspaces(token);
      setAsanaWorkspaces(ws);
      if (ws.length === 0) setApiError('Connected, but no Asana workspaces are visible.');
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to connect to Asana.');
    } finally { setApiLoading(false); }
  };
  const loadAsanaProjects = async (wsGid: string) => {
    setSelAsanaWs(wsGid); setSelAsanaProject(''); setExternalReady(false);
    setApiError(null); setApiLoading(true);
    try { setAsanaProjects(await fetchAsanaProjects(token, wsGid)); }
    catch (e) { setApiError(e instanceof Error ? e.message : 'Failed to load projects.'); }
    finally { setApiLoading(false); }
  };
  const loadAsanaTasks = async () => {
    if (!selAsanaProject) return;
    setApiError(null); setApiLoading(true); setExternalReady(false);
    try {
      const { headers: h, rows: r } = await fetchAsanaTasks(token, selAsanaProject);
      setHeaders(h); setParsedRows(r); setExternalReady(h.length > 0);
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to load Asana tasks.');
    } finally { setApiLoading(false); }
  };

  // ── ClickUp ───────────────────────────────────────────────────
  const connectClickUp = async () => {
    setApiError(null); setApiLoading(true); setExternalReady(false);
    try {
      const teams = await fetchClickUpTeams(token);
      setCuTeams(teams);
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to connect to ClickUp.');
    } finally { setApiLoading(false); }
  };
  const loadCuSpaces = async (teamId: string) => {
    setSelCuTeam(teamId); setSelCuSpace(''); setSelCuList(''); setCuSpaces([]); setCuLists([]); setExternalReady(false);
    setApiError(null); setApiLoading(true);
    try { setCuSpaces(await fetchClickUpSpaces(token, teamId)); }
    catch (e) { setApiError(e instanceof Error ? e.message : 'Failed to load ClickUp spaces.'); }
    finally { setApiLoading(false); }
  };
  const loadCuLists = async (spaceId: string) => {
    setSelCuSpace(spaceId); setSelCuList(''); setCuLists([]); setExternalReady(false);
    setApiError(null); setApiLoading(true);
    try { setCuLists(await fetchClickUpLists(token, spaceId)); }
    catch (e) { setApiError(e instanceof Error ? e.message : 'Failed to load ClickUp lists.'); }
    finally { setApiLoading(false); }
  };
  const loadCuTasks = async () => {
    if (!selCuList) return;
    setApiError(null); setApiLoading(true); setExternalReady(false);
    try {
      const { headers: h, rows: r } = await fetchClickUpTasks(token, selCuList);
      setHeaders(h); setParsedRows(r); setExternalReady(h.length > 0);
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to load ClickUp tasks.');
    } finally { setApiLoading(false); }
  };

  // ── Trello ────────────────────────────────────────────────────
  const connectTrello = async () => {
    setApiError(null); setApiLoading(true); setExternalReady(false);
    try {
      const boards = await fetchTrelloBoards({ key: trelloKey.trim(), token: trelloToken.trim() });
      setTrelloBoards(boards);
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to connect to Trello.');
    } finally { setApiLoading(false); }
  };
  const loadTrelloCards = async () => {
    if (!selTrelloBoard) return;
    setApiError(null); setApiLoading(true); setExternalReady(false);
    try {
      const { headers: h, rows: r } = await fetchTrelloCards({ key: trelloKey.trim(), token: trelloToken.trim() }, selTrelloBoard);
      setHeaders(h); setParsedRows(r); setExternalReady(h.length > 0);
    } catch (e) {
      setApiError(e instanceof Error ? e.message : 'Failed to load Trello cards.');
    } finally { setApiLoading(false); }
  };

  const handleContinue = () => {
    if (source === ImportSourceType.Planner) {
      const payload: ConnectionPayload = {
        kind: 'planner',
        planId: selPlan,
        planTitle: plans.find(p => p.id === selPlan)?.title,
      };
      onConnect(payload);
      return;
    }
    // Everything else — Excel, Project XML, Project Online, Monday, etc. —
    // feeds into the shared rowset pathway.
    const sourceLabel =
      source === ImportSourceType.ExcelCSV ? `Excel/CSV: ${fileName}`
      : source === ImportSourceType.ProjectProfessional ? `Project XML: ${fileName}`
      : source === ImportSourceType.ProjectOnline ? `Project Online: ${poProjects.find(p => p.id === selPoProject)?.name ?? ''}`
      : source === ImportSourceType.Monday ? `Monday.com: ${boards.find(b => b.id === selBoard)?.name ?? ''}`
      : source === ImportSourceType.Smartsheet ? `Smartsheet: ${sheets.find(s => s.id === selSheet)?.name ?? ''}`
      : source === ImportSourceType.Jira ? `Jira: ${jiraProjects.find(p => p.key === selJira)?.name ?? selJira}`
      : source === ImportSourceType.Asana ? `Asana: ${asanaProjects.find(p => p.gid === selAsanaProject)?.name ?? ''}`
      : source === ImportSourceType.ClickUp ? `ClickUp: ${cuLists.find(l => l.id === selCuList)?.name ?? ''}`
      : source === ImportSourceType.Trello ? `Trello: ${trelloBoards.find(b => b.id === selTrelloBoard)?.name ?? ''}`
      : '';
    const carriesHierarchy =
      (source === ImportSourceType.ProjectProfessional || source === ImportSourceType.ProjectOnline) && mppTaskMeta.length > 0;
    const payload: ConnectionPayload = {
      kind: 'rowset',
      fileName,
      headers,
      rows: parsedRows,
      sourceLabel,
      // Microsoft Project XML + Project Online carry WBS collections + links.
      ...(carriesHierarchy
        ? { hierarchy: { collections: mppCollections, taskMeta: mppTaskMeta, dependencyCount: mppDependencyCount } }
        : {}),
    };
    onConnect(payload);
  };

  return (
    <div>
      <h3>Connect to Source</h3>

      {source === ImportSourceType.Planner && (
        <div>
          {loading && <div>Loading plans...</div>}
          {!loading && plans.length === 0 && <div style={{ color: 'var(--ink-4)' }}>No Planner plans found for your account.</div>}
          {plans.map(p => (
            <label key={p.id} style={{ display: 'block', padding: '6px 0' }}>
              <input type="radio" name="plan" value={p.id} checked={selPlan === p.id} onChange={() => setSelPlan(p.id)} />
              {' '}{p.title}
            </label>
          ))}
        </div>
      )}

      {source === ImportSourceType.ProjectOnline && (
        <div>
          <div style={{ padding: '8px 12px', background: '#FEF3C7', color: '#B45309', borderRadius: 'var(--r-sm)', marginBottom: 12, fontSize: 12 }}>
            Microsoft Project Online is being retired in September 2026. Import your projects now.
          </div>
          <label>Project Online PWA URL</label>
          <input value={pwaUrl} onChange={e => { setPwaUrl(e.target.value); setExternalReady(false); }}
            placeholder="https://yourtenant.sharepoint.com/sites/pwa" />
          <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4, lineHeight: 1.5 }}>
            Enter your Project Online PWA site URL. You must have Read access to the PWA site. Your existing
            Microsoft 365 login will be used — no additional credentials needed.
          </div>
          <div style={{ marginTop: 10 }}>
            <button className="btn-secondary" disabled={!pwaUrl.trim() || apiLoading} onClick={connectProjectOnline}>
              {apiLoading ? 'Connecting…' : 'Connect'}
            </button>
          </div>

          {apiError && (
            <div style={{ padding: 10, marginTop: 10, background: '#FEE2E2', borderLeft: '3px solid #DC2626', color: '#991B1B', fontSize: 12, borderRadius: 4, lineHeight: 1.5 }}>{apiError}</div>
          )}

          {poProjects.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <label>Project</label>
              <select value={selPoProject} onChange={e => { setSelPoProject(e.target.value); setExternalReady(false); }}>
                <option value="">— Select project —</option>
                {poProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <div style={{ marginTop: 8 }}>
                <button className="btn-secondary" disabled={!selPoProject || apiLoading} onClick={loadProjectOnlineTasks}>
                  {apiLoading ? 'Loading…' : 'Load tasks'}
                </button>
              </div>
              {externalReady && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>
                  Loaded {parsedRows.length} task{parsedRows.length === 1 ? '' : 's'} into {mppCollections.length} collection{mppCollections.length === 1 ? '' : 's'}
                  {mppDependencyCount > 0 && ` · ${mppDependencyCount} predecessor link${mppDependencyCount === 1 ? '' : 's'}`}.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {source === ImportSourceType.ProjectProfessional && (
        <div>
          <FileDrop
            accept=".mpp,.mpt,.xml"
            fileName={fileName}
            onFile={handleFile}
          />
          {fileKind === 'xml' && !parseError && parsedRows.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--ink-3)' }}>
              Parsed {parsedRows.length} task{parsedRows.length === 1 ? '' : 's'} into {mppCollections.length} collection{mppCollections.length === 1 ? '' : 's'}
              {mppDependencyCount > 0 && ` · ${mppDependencyCount} predecessor link${mppDependencyCount === 1 ? '' : 's'}`}
            </div>
          )}
          {parseError && (
            <div style={{ padding: 10, marginTop: 10, background: '#FEF3C7', borderLeft: '3px solid #D97706', color: '#92400E', fontSize: 12, borderRadius: 4, lineHeight: 1.5 }}>
              {parseError}
            </div>
          )}
        </div>
      )}

      {source === ImportSourceType.ExcelCSV && (
        <div>
          <FileDrop accept=".xlsx,.xls,.csv" fileName={fileName} onFile={handleFile} />
          {parsedRows.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>{parsedRows.length} rows found, {headers.length} columns</div>
          )}
          {parseError && (
            <div style={{ padding: 10, marginTop: 10, background: '#FEF3C7', borderLeft: '3px solid #D97706', color: '#92400E', fontSize: 12, borderRadius: 4 }}>
              {parseError}
            </div>
          )}
        </div>
      )}

      {(source === ImportSourceType.Monday || source === ImportSourceType.Smartsheet) && (
        <div>
          <label>API Token</label>
          <input type="password" value={token} onChange={e => setToken(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                if (source === ImportSourceType.Monday) connectMonday();
                else connectSmartsheet();
              }
            }}
          />
          <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4 }}>
            {source === ImportSourceType.Monday
              ? 'Find at monday.com → Profile → Developers → My Access Tokens'
              : 'Find at app.smartsheet.com → Account → Personal Settings → API Access'}
            . Tokens are held in memory only — never persisted.
          </div>

          <div style={{ marginTop: 10 }}>
            <button className="btn-secondary" disabled={!token.trim() || apiLoading}
              onClick={() => (source === ImportSourceType.Monday ? connectMonday() : connectSmartsheet())}>
              {apiLoading ? 'Connecting…' : 'Connect'}
            </button>
          </div>

          {apiError && (
            <div style={{ padding: 10, marginTop: 10, background: '#FEE2E2', borderLeft: '3px solid #DC2626', color: '#991B1B', fontSize: 12, borderRadius: 4, lineHeight: 1.5 }}>
              {apiError}
              {apiError.toLowerCase().includes('cors') || apiError.toLowerCase().includes('failed to fetch')
                ? ' — browser CORS may be blocking the request. Contact support to enable a server-side connector.'
                : ''}
            </div>
          )}

          {source === ImportSourceType.Monday && boards.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <label>Board</label>
              <select value={selBoard} onChange={e => { setSelBoard(e.target.value); setExternalReady(false); }}>
                <option value="">— Select board —</option>
                {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <div style={{ marginTop: 8 }}>
                <button className="btn-secondary" disabled={!selBoard || apiLoading} onClick={loadMondayBoard}>
                  {apiLoading ? 'Loading…' : 'Load board items'}
                </button>
              </div>
              {externalReady && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>
                  Loaded {parsedRows.length} item{parsedRows.length === 1 ? '' : 's'}, {headers.length} columns.
                </div>
              )}
            </div>
          )}

          {source === ImportSourceType.Smartsheet && sheets.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <label>Sheet</label>
              <select value={selSheet} onChange={e => { setSelSheet(e.target.value); setExternalReady(false); }}>
                <option value="">— Select sheet —</option>
                {sheets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <div style={{ marginTop: 8 }}>
                <button className="btn-secondary" disabled={!selSheet || apiLoading} onClick={loadSmartsheetSheet}>
                  {apiLoading ? 'Loading…' : 'Load sheet'}
                </button>
              </div>
              {externalReady && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>
                  Loaded {parsedRows.length} row{parsedRows.length === 1 ? '' : 's'}, {headers.length} columns.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Jira ─────────────────────────────────────────── */}
      {source === ImportSourceType.Jira && (
        <div>
          <label>Atlassian site domain</label>
          <input value={jiraDomain} onChange={e => setJiraDomain(e.target.value)} placeholder="yourcompany.atlassian.net" />
          <label style={{ marginTop: 10 }}>Atlassian email</label>
          <input type="email" value={jiraEmail} onChange={e => setJiraEmail(e.target.value)} placeholder="you@example.com" />
          <label style={{ marginTop: 10 }}>API Token</label>
          <input type="password" value={token} onChange={e => setToken(e.target.value)} />
          <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4 }}>
            Find at id.atlassian.com → Security → Create and manage API tokens. Held in memory only.
          </div>
          <div style={{ marginTop: 10 }}>
            <button className="btn-secondary" disabled={!jiraDomain.trim() || !jiraEmail.trim() || !token.trim() || apiLoading} onClick={connectJira}>
              {apiLoading ? 'Connecting…' : 'Connect'}
            </button>
          </div>
          {apiError && (
            <div style={{ padding: 10, marginTop: 10, background: '#FEE2E2', borderLeft: '3px solid #DC2626', color: '#991B1B', fontSize: 12, borderRadius: 4 }}>{apiError}</div>
          )}
          {jiraProjects.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <label>Project</label>
              <select value={selJira} onChange={e => { setSelJira(e.target.value); setExternalReady(false); }}>
                <option value="">— Select project —</option>
                {jiraProjects.map(p => <option key={p.key} value={p.key}>{p.name} ({p.key})</option>)}
              </select>
              <div style={{ marginTop: 8 }}>
                <button className="btn-secondary" disabled={!selJira || apiLoading} onClick={loadJiraIssues}>
                  {apiLoading ? 'Loading…' : 'Load issues'}
                </button>
              </div>
              {externalReady && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>Loaded {parsedRows.length} issue{parsedRows.length === 1 ? '' : 's'}.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Asana ────────────────────────────────────────── */}
      {source === ImportSourceType.Asana && (
        <div>
          <label>Personal Access Token</label>
          <input type="password" value={token} onChange={e => setToken(e.target.value)} />
          <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4 }}>
            Find at app.asana.com → My Profile Settings → Apps → Developer Apps → Create a Personal Access Token.
          </div>
          <div style={{ marginTop: 10 }}>
            <button className="btn-secondary" disabled={!token.trim() || apiLoading} onClick={connectAsana}>
              {apiLoading ? 'Connecting…' : 'Connect'}
            </button>
          </div>
          {apiError && (
            <div style={{ padding: 10, marginTop: 10, background: '#FEE2E2', borderLeft: '3px solid #DC2626', color: '#991B1B', fontSize: 12, borderRadius: 4 }}>{apiError}</div>
          )}
          {asanaWorkspaces.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <label>Workspace</label>
              <select value={selAsanaWs} onChange={e => loadAsanaProjects(e.target.value)}>
                <option value="">— Select workspace —</option>
                {asanaWorkspaces.map(w => <option key={w.gid} value={w.gid}>{w.name}</option>)}
              </select>
            </div>
          )}
          {asanaProjects.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <label>Project</label>
              <select value={selAsanaProject} onChange={e => { setSelAsanaProject(e.target.value); setExternalReady(false); }}>
                <option value="">— Select project —</option>
                {asanaProjects.map(p => <option key={p.gid} value={p.gid}>{p.name}</option>)}
              </select>
              <div style={{ marginTop: 8 }}>
                <button className="btn-secondary" disabled={!selAsanaProject || apiLoading} onClick={loadAsanaTasks}>
                  {apiLoading ? 'Loading…' : 'Load tasks'}
                </button>
              </div>
              {externalReady && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>Loaded {parsedRows.length} task{parsedRows.length === 1 ? '' : 's'}.</div>}
            </div>
          )}
        </div>
      )}

      {/* ── ClickUp ──────────────────────────────────────── */}
      {source === ImportSourceType.ClickUp && (
        <div>
          <label>API Token</label>
          <input type="password" value={token} onChange={e => setToken(e.target.value)} />
          <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4 }}>
            Find at app.clickup.com → Settings → Apps → Generate Personal API Token.
          </div>
          <div style={{ marginTop: 10 }}>
            <button className="btn-secondary" disabled={!token.trim() || apiLoading} onClick={connectClickUp}>
              {apiLoading ? 'Connecting…' : 'Connect'}
            </button>
          </div>
          {apiError && (
            <div style={{ padding: 10, marginTop: 10, background: '#FEE2E2', borderLeft: '3px solid #DC2626', color: '#991B1B', fontSize: 12, borderRadius: 4 }}>{apiError}</div>
          )}
          {cuTeams.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <label>Workspace</label>
              <select value={selCuTeam} onChange={e => loadCuSpaces(e.target.value)}>
                <option value="">— Select workspace —</option>
                {cuTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          {cuSpaces.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <label>Space</label>
              <select value={selCuSpace} onChange={e => loadCuLists(e.target.value)}>
                <option value="">— Select space —</option>
                {cuSpaces.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          {cuLists.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <label>List</label>
              <select value={selCuList} onChange={e => { setSelCuList(e.target.value); setExternalReady(false); }}>
                <option value="">— Select list —</option>
                {cuLists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <div style={{ marginTop: 8 }}>
                <button className="btn-secondary" disabled={!selCuList || apiLoading} onClick={loadCuTasks}>
                  {apiLoading ? 'Loading…' : 'Load tasks'}
                </button>
              </div>
              {externalReady && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>Loaded {parsedRows.length} task{parsedRows.length === 1 ? '' : 's'}.</div>}
            </div>
          )}
        </div>
      )}

      {/* ── Trello ───────────────────────────────────────── */}
      {source === ImportSourceType.Trello && (
        <div>
          <label>API Key</label>
          <input value={trelloKey} onChange={e => setTrelloKey(e.target.value)} />
          <label style={{ marginTop: 10 }}>API Token</label>
          <input type="password" value={trelloToken} onChange={e => setTrelloToken(e.target.value)} />
          <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 4 }}>
            Get both at trello.com/power-ups/admin → New power-up or via Developer API Keys page.
          </div>
          <div style={{ marginTop: 10 }}>
            <button className="btn-secondary" disabled={!trelloKey.trim() || !trelloToken.trim() || apiLoading} onClick={connectTrello}>
              {apiLoading ? 'Connecting…' : 'Connect'}
            </button>
          </div>
          {apiError && (
            <div style={{ padding: 10, marginTop: 10, background: '#FEE2E2', borderLeft: '3px solid #DC2626', color: '#991B1B', fontSize: 12, borderRadius: 4 }}>{apiError}</div>
          )}
          {trelloBoards.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <label>Board</label>
              <select value={selTrelloBoard} onChange={e => { setSelTrelloBoard(e.target.value); setExternalReady(false); }}>
                <option value="">— Select board —</option>
                {trelloBoards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <div style={{ marginTop: 8 }}>
                <button className="btn-secondary" disabled={!selTrelloBoard || apiLoading} onClick={loadTrelloCards}>
                  {apiLoading ? 'Loading…' : 'Load cards'}
                </button>
              </div>
              {externalReady && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>Loaded {parsedRows.length} card{parsedRows.length === 1 ? '' : 's'}.</div>}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <button className="btn-primary" onClick={handleContinue} disabled={!readyToConnect()}>Continue</button>
      </div>
    </div>
  );
}

function FileDrop({ accept, fileName, onFile }: { accept: string; fileName: string; onFile: (f: File) => void }) {
  return (
    <div
      onDrop={e => { e.preventDefault(); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}
      onDragOver={e => e.preventDefault()}
      style={{ border: '2px dashed var(--border)', borderRadius: 'var(--r-md)', padding: 40, textAlign: 'center', color: 'var(--ink-4)', cursor: 'pointer', background: 'var(--surface-2)' }}
      onClick={() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        input.onchange = () => { if (input.files?.[0]) onFile(input.files[0]); };
        input.click();
      }}
    >
      {fileName ? `Selected: ${fileName}` : `Drop a file here or click to browse — accepts ${accept}`}
    </div>
  );
}
